/**
 * Capa de datos aislada.
 *
 * La interfaz de la aplicación SOLO habla con este módulo. Nunca importa Firestore
 * ni toca `localStorage` directamente. Cambiar de motor de almacenamiento es
 * reemplazar la implementación de abajo; ni un componente se entera.
 *
 * Ésta es la razón por la que el prototipo pudo empezar en `localStorage` y pasar a
 * Firestore sin reescribir la interfaz. Si esta separación se pierde, la próxima
 * migración se hace persiguiendo llamadas por todo el código.
 *
 * Colecciones: clientes · cotizaciones · fotos · catalogoProductos ·
 * catalogoDispositivos · config
 */

import * as local from './local.js'
import { tamanoJSON, TOPE_DOCUMENTO, CENTINELA_BORRAR } from './fusion.js'
export { CENTINELA_BORRAR }

// Mientras no haya credenciales de Firebase, la aplicación corre contra el navegador.
// Con emulador o con proyecto real, `VITE_MOTOR=firestore` cambia el motor.
//
// La implementación de Firestore se carga de forma DINÁMICA para que el SDK de
// Firebase —que pesa medio megabyte— no viaje en el paquete cuando no se usa.
const usaFirestore = (import.meta.env?.VITE_MOTOR || 'local') === 'firestore'
const MOTOR = usaFirestore ? await import('./firestore.js') : local

/* ---------- Las seis funciones ---------- */

/** Devuelve todos los documentos de una colección. */
export const listar = coleccion => MOTOR.listar(coleccion)

/** Devuelve un documento por id, o null. */
export const obtener = (coleccion, id) => MOTOR.obtener(coleccion, id)

/* GUARDA DE TAMAÑO (E0): Firestore rechaza documentos de 1 MiB con un error
   críptico y ya en la nube. Aquí se rechaza antes, con el peso en la cara y la
   causa probable. Casi siempre son fotos incrustadas o un diseño copiado entero. */
function comprobarTamano(objeto) {
  const bytes = tamanoJSON(objeto)
  if (bytes > TOPE_DOCUMENTO)
    throw new Error(`Este documento pesa ${Math.round(bytes / 1024)} KB y el tope es ${Math.round(TOPE_DOCUMENTO / 1024)} KB. ` +
      'Casi siempre es una foto incrustada o un diseño copiado entero: revísalo antes de guardar.')
}

/** Crea o FUSIONA (mapas clave por clave, arreglos sustituidos). Si el objeto no
    trae id, se le asigna uno. Devuelve el id. */
export const guardar = (coleccion, objeto) => { comprobarTamano(objeto); return MOTOR.guardar(coleccion, objeto) }

/** Parche por id: solo cambian las rutas que vienen en `parche`. Es la escritura
    correcta para todo lo que dos dispositivos pueden editar a la vez. */
export const guardarParcial = (coleccion, id, parche) => { comprobarTamano(parche); return MOTOR.guardarParcial(coleccion, id, parche) }

/** Borra una ruta con punto (`plano.riego`) dentro de un documento. */
export const borrarCampo = (coleccion, id, ruta) => MOTOR.borrarCampo(coleccion, id, ruta)

/** Borra un documento por id. */
export const borrar = (coleccion, id) => MOTOR.borrar(coleccion, id)

/** Escucha cambios. Devuelve la función para dejar de escuchar. */
export const suscribir = (coleccion, callback) => MOTOR.suscribir(coleccion, callback)

/** Escucha SOLO los documentos donde `campo == valor`. Es la vía del rol aliado:
    sus reglas rechazan la colección completa, la consulta filtrada sí pasa. */
export const suscribirConsulta = (coleccion, campo, valor, callback) =>
  MOTOR.suscribirConsulta(coleccion, campo, valor, callback)

/**
 * Guarda una foto ya comprimida y devuelve su id.
 * Las fotos viven como documentos propios y se referencian desde la cotización:
 * nunca incrustadas en ella, para no inflar el documento que se lee entero.
 */
export const subirFoto = (dataUrl, meta) => MOTOR.subirFoto(dataUrl, meta)

/** Recupera el contenido de una foto por id. */
export const obtenerFoto = id => MOTOR.obtenerFoto(id)

/* ---------- Estado de sincronización ----------
 * Con trabajo sin conexión, el vendedor tiene que saber si lo que capturó en el
 * sótano ya subió o sigue solo en el teléfono. Sin este aviso, el modo sin conexión
 * es una trampa, no una ventaja.
 */
export const estadoSync = callback => MOTOR.estadoSync(callback)

export const motorActivo = () => (usaFirestore ? 'firestore' : 'local')

/* ---------- Utilidades compartidas ---------- */

export const nuevoId = () =>
  Date.now().toString(36) + Math.random().toString(36).slice(2, 8)

/**
 * Comprime una imagen antes de guardarla. A 420 px y 72% de calidad una foto de
 * levantamiento pesa entre 40 y 80 KB, muy por debajo del tope de 1 MiB por
 * documento de Firestore.
 */
export function comprimirImagen(file, ladoMaximo = 420, calidad = 0.72) {
  return new Promise((resolve, reject) => {
    const lector = new FileReader()
    lector.onerror = () => reject(new Error('No se pudo leer el archivo'))
    lector.onload = () => {
      const img = new Image()
      img.onerror = () => reject(new Error('El archivo no es una imagen válida'))
      img.onload = () => {
        const escala = Math.min(1, ladoMaximo / Math.max(img.width, img.height))
        const lienzo = document.createElement('canvas')
        lienzo.width = Math.round(img.width * escala)
        lienzo.height = Math.round(img.height * escala)
        lienzo.getContext('2d').drawImage(img, 0, 0, lienzo.width, lienzo.height)
        resolve(lienzo.toDataURL('image/jpeg', calidad))
      }
      img.src = lector.result
    }
    lector.readAsDataURL(file)
  })
}
